import { getPreviousSiblingBackground } from './getPreviousSiblingBackground';

/**
 * Observes the parent of `element` for child-list changes and size changes,
 * calling `onUpdate` with the resolved background colour of the last sibling
 * whenever the DOM structure or layout changes.
 *
 * Calls `onUpdate` once immediately on setup, then re-calls on every
 * childList mutation or resize so the colour stays in sync if children are
 * added, removed, or finish painting at runtime.
 */
export const observeLastSiblingBackground = (element, onUpdate) => {
  const update = () => onUpdate(getPreviousSiblingBackground(element));

  // ResizeObserver fires after layout — at that point getComputedStyle has
  // resolved CSS custom properties like var(--material), so we get the real colour.
  const resizeObserver = new ResizeObserver(update);

  const observedSiblings = new Set();

  const syncResizeTargets = () => {
    Array.from(element.parentElement.children).forEach(child => {
      if (child !== element && !observedSiblings.has(child)) {
        resizeObserver.observe(child);
        observedSiblings.add(child);
      }
    });
  };

  update();
  syncResizeTargets();

  const mutationObserver = new MutationObserver(() => {
    syncResizeTargets();
    update();
  });

  mutationObserver.observe(element.parentElement, { childList: true, subtree: false });

  return () => {
    mutationObserver.disconnect();
    resizeObserver.disconnect();
  };
};
